"use client";

import React, { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Database, MessageSquare, FileSpreadsheet, Mail, PhoneCall, AlertCircle, ChevronDown } from "lucide-react";
import { DiscoveryCategory } from "./ContextualDiscoveryModal";

interface Act1TheBusinessProps {
  onOpenDiscovery: (category?: DiscoveryCategory) => void;
}

export const Act1TheBusiness: React.FC<Act1TheBusinessProps> = ({ onOpenDiscovery }) => {
  // Individual section refs so scroll calculations only run when section is in view
  const heroRef = useRef<HTMLDivElement>(null);
  const receptionRef = useRef<HTMLDivElement>(null);
  const escalatorRef = useRef<HTMLDivElement>(null);

  // Scene 1: Hero camera approach (isolated to hero section for max FPS)
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroScale = useTransform(heroScroll, [0, 1], [1, 1.08]);
  const heroY = useTransform(heroScroll, [0, 1], ["0%", "4%"]);

  // Scene 2: Reception forward camera push (isolated to reception section)
  const { scrollYProgress: receptionScroll } = useScroll({
    target: receptionRef,
    offset: ["start end", "end start"],
  });
  const receptionScale = useTransform(receptionScroll, [0, 1], [1.02, 1.07]);

  // Scene 4: Escalator upward perspective ascent (isolated to escalator section)
  const { scrollYProgress: escalatorScroll } = useScroll({
    target: escalatorRef,
    offset: ["start end", "end start"],
  });
  const escalatorY = useTransform(escalatorScroll, [0, 1], ["4%", "-6%"]);

  return (
    <div className="relative w-full text-[#111318]">
      {/* =========================================================================
          SCENE 01: ARRIVAL — FULL-SCREEN ARCHITECTURAL HERO CANVAS
      ========================================================================= */}
      <section
        ref={heroRef}
        id="scene-outside"
        className="relative min-h-screen w-full flex items-center justify-start px-6 sm:px-12 lg:px-20 py-24 overflow-hidden border-b border-[#111318]/10 bg-slate-900"
      >
        {/* Full-Screen Real Architectural Photograph: GPU-accelerated smooth scale */}
        <motion.div
          style={{ scale: heroScale, y: heroY }}
          className="absolute inset-0 z-0 origin-center will-change-transform transform-gpu"
        >
          <Image
            src="/images/storytelling/office/exterior.webp"
            alt="Johnson Softwares Bengaluru Technology Headquarters Tower"
            fill
            priority
            sizes="100vw"
            quality={85}
            className="object-cover object-center"
          />
        </motion.div>

        {/* Subtle, localized radial text shade behind headline (Leaves 75%+ of photo untouched) */}
        <div className="absolute inset-y-0 left-0 w-full sm:w-[60%] lg:w-[48%] bg-gradient-to-r from-white/92 via-white/70 to-transparent pointer-events-none z-[1]" />

        {/* Narrator Typography Sitting in Natural Upper-Left Negative Space */}
        <div className="relative z-10 max-w-2xl text-left">
          {/* Telemetry Badge */}
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-xs text-xs font-mono text-[#111318] mb-6">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
            <span className="tracking-wide font-medium">BENGALURU HEADQUARTERS // CAMPUS TOWER</span>
          </div>

          <div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#111318] tracking-tight leading-[1.08] mb-6 drop-shadow-xs">
              YOUR BUSINESS IS GROWING.
              <br />
              <span className="text-[#2563EB]">
                YOUR SOFTWARE SHOULD GROW WITH IT.
              </span>
            </h1>

            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-8 max-w-xl font-normal">
              Custom software, intelligent automation, and operational systems engineered around the way your business actually works — without per-seat SaaS taxes or brittle workarounds.
            </p>

            {/* High-Contrast Primary Call-to-Actions Above The Fold */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 mb-10">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-bold text-base transition-all shadow-[0_4px_20px_rgba(37,99,235,0.4)] flex items-center justify-center gap-2 group cursor-pointer"
              >
                <span>Request a 10-Minute Demo</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                href="/contact?subject=Free+Quote+Inquiry"
                className="px-8 py-4 rounded-xl bg-white/95 hover:bg-white border-2 border-[#111318]/15 hover:border-[#2563EB] text-[#111318] hover:text-[#2563EB] font-bold text-base transition-all text-center shadow-xs cursor-pointer"
              >
                Get a Free Quote
              </Link>
            </div>

            {/* Quiet Credibility Metrics */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono text-[#374151] pt-4 border-t border-[#111318]/10">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-[#2563EB]" />
                <span>100% Client Code &amp; DB Ownership</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5 text-[#2563EB]" />
                <span>Zero Per-Seat SaaS Taxes</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#2563EB]" />
                <span>Direct Principal Architecture</span>
              </span>
            </div>
          </div>
        </div>

        {/* Scroll Prompt Arrow */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-xs font-mono text-[#111318]/70 pointer-events-none">
          <span className="tracking-widest uppercase text-[10px]">SCROLL TO ENTER</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>

        {/* Floating Building Telemetry Badge (Bottom Right) */}
        <div className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-md text-[11px] font-mono text-[#111318]">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span>FACILITY STATUS // OPERATIONAL</span>
        </div>
      </section>

      {/* =========================================================================
          SCENE 02: RECEPTION — FULL-SCREEN ENTRANCE CANVAS
      ========================================================================= */}
      <section
        ref={receptionRef}
        id="scene-reception"
        className="relative min-h-screen w-full flex items-center justify-start px-6 sm:px-12 lg:px-20 py-24 overflow-hidden border-b border-[#111318]/10 bg-slate-900"
      >
        {/* Real Reception Lobby Photograph: GPU-accelerated smooth push */}
        <motion.div
          style={{ scale: receptionScale }}
          className="absolute inset-0 z-0 origin-center will-change-transform transform-gpu"
        >
          <Image
            src="/images/storytelling/office/reception.webp"
            alt="Johnson Softwares Corporate Reception Lobby and Executive Corridor"
            fill
            sizes="100vw"
            quality={85}
            loading="lazy"
            className="object-cover object-center"
          />
        </motion.div>

        {/* Localized subtle left gradient behind text (Leaves 75% of lobby & corridor completely unshaded) */}
        <div className="absolute inset-y-0 left-0 w-full sm:w-[55%] lg:w-[45%] bg-gradient-to-r from-white/92 via-white/70 to-transparent pointer-events-none z-[1]" />

        {/* Narrator Typography in Natural Negative Space */}
        <div className="relative z-10 max-w-xl text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#2563EB]/10 border border-[#2563EB]/20 text-xs font-mono text-[#2563EB] font-semibold mb-4">
            <span>RECEPTION // ARCHITECTURAL PHILOSOPHY</span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight leading-tight mb-5">
            EVERY BUSINESS HAS A UNIQUE WAY OF WORKING.
            <br />
            <span className="text-[#2563EB]">
              WE BUILD THE SOFTWARE AROUND IT.
            </span>
          </h2>

          <p className="text-base text-[#374151] leading-relaxed mb-6">
            Off-the-shelf SaaS forces your staff to adapt their daily operations to rigid templates. We engineer systems that conform to your exact business logic.
          </p>

          {/* Clean Capability Markers Sitting Directly in the Scene */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-mono text-[#111318] pt-4 border-t border-[#111318]/10">
            <span className="font-bold text-[#2563EB]">CUSTOM SOFTWARE</span>
            <span className="text-[#111318]/25">•</span>
            <span className="font-semibold text-[#111318]">AUTOMATION</span>
            <span className="text-[#111318]/25">•</span>
            <span className="font-semibold text-[#111318]">AI AGENTS</span>
            <span className="text-[#111318]/25">•</span>
            <span className="font-semibold text-[#111318]">CRM / ERP</span>
            <span className="text-[#111318]/25">•</span>
            <span className="font-bold text-[#2563EB]">100% IP OWNERSHIP</span>
          </div>
        </div>

        {/* Floating Architectural Badge */}
        <div className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-md text-[11px] font-mono text-[#111318]">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span>RECEPTION &amp; EXECUTIVE CORRIDOR</span>
        </div>
      </section>

      {/* =========================================================================
          SCENE 03: THE OPERATIONAL REALITY — INFORMATION LAYERS IN ENVIRONMENT
      ========================================================================= */}
      <section
        id="scene-problem"
        className="relative min-h-screen w-full flex flex-col justify-center py-24 px-6 sm:px-8 lg:px-16 overflow-hidden border-b border-[#111318]/10 bg-slate-950 text-[#111318]"
      >
        {/* Real Corridor / Workspace Environment Visible Behind Information Layers */}
        <div className="absolute inset-0 z-0 opacity-35">
          <Image
            src="/images/storytelling/office/corridor.webp"
            alt="Office operational corridor"
            fill
            sizes="100vw"
            quality={80}
            loading="lazy"
            className="object-cover object-center"
          />
        </div>

        {/* Fast clean solid overlay without GPU-taxing backdrop-blur */}
        <div className="absolute inset-0 z-[1] bg-[#F8F8F6]/92 pointer-events-none" />

        <div className="relative z-10 w-full max-w-6xl mx-auto">
          {/* Narrator Headlines */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-mono tracking-widest text-[#2563EB] uppercase block mb-3 font-semibold">
              THE OPERATIONAL REALITY
            </span>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight mb-4">
              GROWTH CREATES COMPLEXITY.
            </h2>
            <p className="text-base text-[#5E636B] leading-relaxed">
              When a business starts, a notebook or basic spreadsheet works. But as customer volume multiplies, operational data splinters across disconnected tools.
            </p>
          </div>

          {/* Floating Information Layers in the Physical Space */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-12">
            {/* The Multiplication of Fragmented Tools */}
            <div className="p-8 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_12px_40px_rgb(0,0,0,0.06)] space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-[#111318]/10 text-xs font-mono">
                <span className="text-[#5E636B] font-medium">DISCONNECTED REALITY</span>
                <span className="text-rose-600 font-semibold">6+ SEPARATE TOOLS</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex items-center gap-2.5">
                  <MessageSquare className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#111318]">WhatsApp Web</div>
                    <div className="text-[10px] text-[#5E636B]">Unlogged chats</div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex items-center gap-2.5">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#111318]">Google Sheets</div>
                    <div className="text-[10px] text-[#5E636B]">Outdated rows</div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-blue-600 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#111318]">Shared Inboxes</div>
                    <div className="text-[10px] text-[#5E636B]">Missed leads</div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex items-center gap-2.5">
                  <PhoneCall className="w-4 h-4 text-amber-600 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#111318]">Manual Calls</div>
                    <div className="text-[10px] text-[#5E636B]">No recordings</div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex items-center gap-2.5">
                  <Database className="w-4 h-4 text-indigo-600 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#111318]">Generic SaaS</div>
                    <div className="text-[10px] text-[#5E636B]">₹4,000/seat/mo</div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#111318]">Sticky Notes</div>
                    <div className="text-[10px] text-[#5E636B]">Lost follow-ups</div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-[#5E636B] pt-2 leading-relaxed">
                Staff spend 35% of their working hours manually copying customer numbers across tabs, updating spreadsheets, and re-keying invoices.
              </p>
            </div>

            {/* The Bottleneck Chain */}
            <div className="p-8 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_12px_40px_rgb(0,0,0,0.06)]">
              <span className="text-xs font-mono text-[#5E636B] tracking-wider uppercase block mb-4 font-semibold">
                THE BOTTLENECK CHAIN
              </span>

              <div className="space-y-3 font-mono text-xs">
                {[
                  { stage: "LEADS", issue: "Arrive via ads & website at 9:00 PM when staff is offline" },
                  { stage: "CUSTOMERS", issue: "Wait 14 hours for a response; buy from competitors instead" },
                  { stage: "EMPLOYEES", issue: "Drowning in manual coordination across disconnected tabs" },
                  { stage: "ORDERS", issue: "Tracked in chat threads without automated status updates" },
                  { stage: "OPERATIONS", issue: "Leadership lacks unified, real-time pipeline clarity" },
                ].map((item, i) => (
                  <div key={item.stage} className="flex items-start gap-3 p-3 rounded-xl bg-[#F8F8F6] border border-[#111318]/5">
                    <span className="w-6 h-6 rounded-md bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center font-bold shrink-0">
                      0{i + 1}
                    </span>
                    <div>
                      <span className="font-bold text-[#111318]">{item.stage}: </span>
                      <span className="text-[#5E636B]">{item.issue}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SCENE 04: ESCALATOR — FULL-SCREEN ASCENT PERSPECTIVE CANVAS
      ========================================================================= */}
      <section
        ref={escalatorRef}
        id="scene-escalator"
        className="relative min-h-screen w-full flex items-end justify-center pb-24 px-6 sm:px-8 overflow-hidden border-b border-[#111318]/10 bg-slate-950"
      >
        {/* Real Architectural Escalator Photograph: GPU-accelerated smooth ascent */}
        <motion.div
          style={{ y: escalatorY }}
          className="absolute inset-0 z-0 origin-bottom will-change-transform transform-gpu"
        >
          <Image
            src="/images/storytelling/office/escalator-architecture.webp"
            alt="Corporate modern architectural escalator"
            fill
            sizes="100vw"
            quality={85}
            loading="lazy"
            className="object-cover object-center"
          />
        </motion.div>

        {/* Subtle, localized radial dark fade at the bottom to ground the white typography */}
        <div className="absolute inset-x-0 bottom-0 h-96 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none z-[1]" />

        {/* Minimal Narrative Typography Sitting Directly in Clean Architectural Negative Space */}
        <div className="relative z-10 w-full max-w-2xl text-center text-white">
          <span className="text-xs font-mono tracking-widest text-blue-400 uppercase block font-semibold mb-3">
            LEVEL 2 TRANSITION
          </span>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight mb-4 drop-shadow-md">
            MOVING FROM OPERATIONAL CHAOS
            <br />
            <span className="text-blue-400">TO ENGINEERED CERTAINTY.</span>
          </h2>

          <p className="text-sm sm:text-base text-slate-300 max-w-lg mx-auto mb-8 leading-relaxed">
            Ascending to Level 2 engineering studios. Step directly into our web, mobile, and CRM operations floors.
          </p>

          <div>
            <a
              href="#scene-studio-web"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-600 text-white text-sm font-semibold transition-all shadow-[0_4px_16px_rgba(37,99,235,0.4)]"
            >
              <span>ENTER SPECIALIST STUDIOS</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Floating Architectural Ascent Badge */}
        <div className="absolute top-8 right-8 z-10 hidden sm:flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-md text-[11px] font-mono text-[#111318]">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span>ASCENDING TO LEVEL 2 // ENGINEERING STUDIOS</span>
        </div>
      </section>
    </div>
  );
};
